import { useRef, useState, useCallback, type ReactNode } from "react";
import { Trash2 } from "lucide-react";
import { useHaptics } from "@/hooks/use-haptics";

interface SwipeToDeleteProps {
  onDelete: () => void;
  children: ReactNode;
}

const THRESHOLD = 80;

const SwipeToDelete = ({ onDelete, children }: SwipeToDeleteProps) => {
  const startX = useRef(0);
  const currentX = useRef(0);
  const swiping = useRef(false);
  const passedThreshold = useRef(false);
  const [offset, setOffset] = useState(0);
  const [removing, setRemoving] = useState(false);
  const { lightTap, warning } = useHaptics();

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    swiping.current = true;
    passedThreshold.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!swiping.current) return;
    currentX.current = e.touches[0].clientX;
    const diff = startX.current - currentX.current;
    const clamped = Math.max(0, Math.min(diff, 120));
    setOffset(clamped);
    // Haptic tick when crossing the threshold
    if (clamped >= THRESHOLD && !passedThreshold.current) {
      passedThreshold.current = true;
      lightTap();
    } else if (clamped < THRESHOLD) {
      passedThreshold.current = false;
    }
  }, [lightTap]);

  const handleTouchEnd = useCallback(() => {
    swiping.current = false;
    if (offset >= THRESHOLD) {
      setRemoving(true);
      warning();
      setTimeout(() => onDelete(), 250);
    } else {
      setOffset(0);
    }
  }, [offset, onDelete, warning]);

  return (
    <div className="relative overflow-hidden rounded-lg" style={{ opacity: removing ? 0 : 1, maxHeight: removing ? 0 : 200, transition: "opacity 0.25s, max-height 0.25s" }}>
      {/* Delete background */}
      <div className="absolute inset-0 bg-destructive flex items-center justify-end pr-5 rounded-lg">
        <Trash2 className="w-4 h-4 text-destructive-foreground" />
      </div>
      {/* Swipeable content */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateX(-${offset}px)`,
          transition: swiping.current ? "none" : "transform 0.2s ease-out",
        }}
        className="relative z-10"
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeToDelete;
